// Does this person have an accountant, and is that accountant waiting on them?
// (PRD 63 §5.2)
//
// THE GAP THIS CLOSES: the client's side of PRD 63 shipped with exactly one
// door — a card on the business dashboard that renders NOTHING when nothing is
// outstanding. So a client with no books never saw it at all, and a client who
// answered everything lost the screen, the history and every request they had
// ever been sent the moment they finished. The same class of bug the Practice
// section had ([[accountant-hq-was-unreachable]]): not a screen with no nav
// row, but a whole surface reachable only by luck.
//
// WHY A MODULE-LEVEL STORE AND NOT A CONTEXT: the answer is needed by
// WebNavigation, which is mounted in AppShell — the PARENT route of
// BusinessShell, where BusinessProvider lives. Nothing a business context owns
// can reach the sidebar, and this fact is not business-scoped anyway. Same
// shape as firmMembershipStore, which asks the mirror-image question.
//
// A COUNT, NOT THE QUEUE. The nav needs a boolean and a badge number; caching
// the requests themselves here would be a second, staler copy of what the
// screen fetches for itself.

import { clientRequestService } from './clientRequestService';

export interface ClientEngagementState {
  /**
   * True when a firm holds an engagement covering this person — whether or not
   * anything is outstanding right now.
   *
   * NOT "has something to do": the row has to keep drawing after the last item
   * comes back, or the screen goes unreachable at the moment they finish
   * answering, which is the dead end this store exists to remove.
   *
   * Null means UNKNOWN: no mirrored answer, and no settled fetch.
   */
  engaged: boolean | null;
  /** Items still waiting on them, across every live request. Drives the badge. */
  outstanding: number;
  /** True once a server answer has settled this session, success or failure. */
  hasLoaded: boolean;
}

type Listener = (state: ClientEngagementState) => void;

const UNKNOWN: ClientEngagementState = { engaged: null, outstanding: 0, hasLoaded: false };

let state: ClientEngagementState = UNKNOWN;
let listeners = new Set<Listener>();
// Which user the in-flight (or settled) fetch belongs to, so signing in as
// somebody else re-asks rather than inheriting the last person's answer.
let loadedFor: string | null = null;

// THE MIRROR: the sidebar renders before any fetch can settle, and without a
// synchronously readable answer a client would watch the section pop in a beat
// late on every page load. Keyed by user uuid rather than cleared on sign-out,
// copying firmMembershipStore exactly — the next person to use this browser
// cannot read the last person's answer even if a clear were missed.
//
// ONLY THE BOOLEAN IS MIRRORED. A stale `engaged` costs one menu row; a stale
// COUNT is a badge that lies about how much work somebody has, so the badge
// waits for the server.
const mirrorKey = (uuid: string | undefined): string | null =>
  uuid ? `gb_has_accountant:${uuid}` : null;

export function readEngagementMirror(uuid: string | undefined): boolean | null {
  const key = mirrorKey(uuid);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : raw === 'true';
  } catch {
    return null;
  }
}

function writeMirror(uuid: string | undefined, value: boolean): void {
  const key = mirrorKey(uuid);
  if (!key) return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Private mode, or storage full. The server still has the answer.
  }
}

export function getClientEngagementState(): ClientEngagementState {
  return state;
}

/** Subscribe to changes; returns the unsubscribe function. */
export function subscribeClientEngagement(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(next: ClientEngagementState): void {
  state = next;
  listeners.forEach(listener => listener(state));
}

/**
 * Ask the server once per signed-in user. Safe to call on every render pass:
 * the second and later calls for the same user do nothing.
 *
 * Seeds from the mirror first so a returning client gets a correct FIRST
 * frame, then lets the server overwrite it — an engagement that ended since
 * the last visit corrects itself on this request rather than persisting.
 */
export function ensureClientEngagementLoaded(uuid: string | undefined): void {
  if (loadedFor === (uuid ?? null)) return;
  loadedFor = uuid ?? null;

  const mirrored = readEngagementMirror(uuid);
  if (mirrored !== null) {
    publish({ engaged: mirrored, outstanding: 0, hasLoaded: false });
  }

  clientRequestService
    .getSummary()
    .then(summary => {
      writeMirror(uuid, summary.engaged);
      publish({
        engaged: summary.engaged,
        outstanding: summary.outstanding_items,
        hasLoaded: true,
      });
    })
    .catch(() => {
      // A failed fetch keeps the mirrored answer rather than reverting to "no
      // accountant": the last known answer is the better guess, and being
      // wrong here costs one menu row, not access to anything.
      publish({ engaged: state.engaged ?? false, outstanding: state.outstanding, hasLoaded: true });
    });
}

/**
 * Something just changed the count — the client answered an item. Republishes
 * without re-asking, so the badge goes down in the same frame as the answer
 * rather than on their next full page load.
 */
export function publishOutstandingCount(outstanding: number): void {
  publish({ engaged: state.engaged ?? true, outstanding, hasLoaded: state.hasLoaded });
}

/**
 * Back to unknown, WITHOUT touching any mirror — the uuid key is what keeps
 * each person's answer to themselves, so sign-out has nothing to erase.
 */
export function clearClientEngagement(): void {
  loadedFor = null;
  publish(UNKNOWN);
}

/** Test hook — drops listeners and state without notifying anybody. */
export function resetClientEngagementStore(): void {
  state = UNKNOWN;
  listeners = new Set();
  loadedFor = null;
}
